import React, { useState, useRef, useEffect } from "react";
import { Card, Input, Button, Alert, Spin, Collapse, Space } from "antd";
import { askAi } from "../../services/aiService";
import "./AIChatBox.css";

const { TextArea } = Input;

const sampleQuestions = [
  "What is the status of registration for jdoe@nvidia.com?",
  "How many registrations were received in the last 24 hours?",
  "How many registrations failed in the last 7 days?"
];

const NVIDIA_GREEN = "#76B900";
const askButtonStyle = { background: NVIDIA_GREEN, borderColor: NVIDIA_GREEN };

export default function AIChatBox({ style, bodyStyle, extra } = {}) {
  const [question, setQuestion] = useState("");
  const [history, setHistory]   = useState([]); // [{ q, answer, toolResults }] — oldest first
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const ask = async (q) => {
    const value = typeof q === "string" ? q : question;
    const trimmed = value.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    try {
      const data = await askAi(trimmed);
      setHistory(function (h) {
        return h.concat([{ q: trimmed, answer: data.answer, toolResults: data.toolResults }]);
      });
      setQuestion("");
    } catch (e) {
      var msg = (e && e.response && e.response.data && e.response.data.error)
        || (e && e.message)
        || "Request failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="Registration assistant"
      style={style || { maxWidth: 900, margin: "16px auto" }}
      bodyStyle={bodyStyle}
      extra={extra}
    >
      <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingRight: 4 }}>
        {error && (
          <Alert
            type="error"
            message={error}
            closable
            onClose={() => setError(null)}
            style={{ marginBottom: 12 }}
          />
        )}

        {history.length === 0 && !loading && (
          <div className="ai-msg-empty">
            Ask anything about registrations.
            <br />
            Try one of the suggestions below.
          </div>
        )}

        {history.map((item, idx) => (
          <div key={idx} className="ai-msg-pair">
            <div className="ai-msg-user">{item.q}</div>

            <div className="ai-msg-ai">
              <div className="ai-msg-avatar">AI</div>
              <div className="ai-msg-ai-wrap">
                <div className="ai-msg-ai-body">{item.answer}</div>

                {item.toolResults && item.toolResults.length > 0 && (
                  <Collapse
                    ghost
                    size="small"
                    className="ai-msg-data"
                    items={[{
                      key: "data",
                      label: `Underlying data (${item.toolResults.length} tool call${item.toolResults.length === 1 ? "" : "s"})`,
                      children: (
                        <pre>{JSON.stringify(item.toolResults, null, 2)}</pre>
                      )
                    }]}
                  />
                )}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <Spin tip="Thinking…" size="small" />
          </div>
        )}
      </div>

      <div style={{ flexShrink: 0, paddingTop: 12, marginTop: 12, borderTop: "1px solid #f0f0f0" }}>
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <TextArea
            rows={2}
            value={question}
            placeholder="Ask about a registration by email, or counts over a time window…"
            onChange={(e) => setQuestion(e.target.value)}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                ask();
              }
            }}
            disabled={loading}
          />

          <Space wrap>
            <Button type="primary" onClick={() => ask()} loading={loading} style={askButtonStyle}>
              Ask
            </Button>
            {sampleQuestions.map((s) => (
              <Button key={s} size="small" onClick={() => ask(s)} disabled={loading}>
                {s}
              </Button>
            ))}
          </Space>
        </Space>
      </div>
    </Card>
  );
}
